import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Mail, KeyRound, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  const [step, setStep] = useState(tokenFromUrl ? 'token' : 'email');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [tokenInput, setTokenInput] = useState(tokenFromUrl || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tokenFromUrl) {
      setTokenInput(tokenFromUrl);
      setStep('token');
    }
  }, [tokenFromUrl]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    trackEvent('forgot_password', '/forgot-password');
    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setTokenInput(response.data.reset_token);
      }
      setStep('token');
      toast.success('Reset token generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: tokenInput,
        new_password: newPassword
      });
      setStep('done');
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            className="inline-flex items-center text-slate-400 hover:text-[#0F172A] mb-10 transition-colors text-sm tracking-wider"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            BACK TO LOGIN
          </Link>

          <div className="mb-10">
            <Link to="/" className="flex items-center gap-3 mb-8 group" data-testid="auth-logo-link" aria-label="Shopperden home">
              <span
                aria-hidden="true"
                className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm transition-transform duration-200 group-hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #0f766e 100%)' }}
              >
                <svg viewBox="0 0 64 64" className="w-6 h-6" fill="none" stroke="#ffffff" strokeLinejoin="round" strokeLinecap="round">
                  <path d="M25 17 Q25 10 32 10 Q39 10 39 17" strokeWidth="2.6" />
                  <path d="M14 32 L32 17 L50 32 L50 50 L14 50 Z" strokeWidth="3.6" />
                  <path d="M28 50 L28 38 L36 38 L36 50" strokeWidth="3.6" />
                </svg>
              </span>
              <span className="font-['Playfair_Display'] text-xl text-[#0F172A] tracking-wide">
                SHOPPER<span className="text-[#E07A3A]">DEN</span>
              </span>
            </Link>

            {step === 'email' && (
              <>
                <h1 className="font-['Playfair_Display'] text-4xl text-[#0F172A] mb-3">
                  Reset Password
                </h1>
                <p className="text-slate-500">
                  Enter your email and we'll send you a reset link
                </p>
              </>
            )}
            {step === 'token' && (
              <>
                <h1 className="font-['Playfair_Display'] text-4xl text-[#0F172A] mb-3">
                  New Password
                </h1>
                <p className="text-slate-500">
                  Enter your reset code and choose a new password
                </p>
              </>
            )}
            {step === 'done' && (
              <>
                <h1 className="font-['Playfair_Display'] text-4xl text-[#0F172A] mb-3">
                  All Set
                </h1>
                <p className="text-slate-500">
                  Your password has been reset successfully
                </p>
              </>
            )}
          </div>

          {/* Step 1: Enter Email */}
          {step === 'email' && (
            <form onSubmit={handleRequestReset} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-600 text-sm tracking-wider uppercase">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-white border-slate-200 text-[#0F172A] placeholder:text-slate-300 h-12 pl-10 focus:border-[#E07A3A] focus:ring-[#E07A3A]"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 text-sm tracking-widest uppercase"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </form>
          )}

          {/* Step 2: Enter Token + New Password */}
          {step === 'token' && (
            <>
              {/* Email sent confirmation */}
              {resetToken && !tokenFromUrl && (
                <div className="mb-6 p-5 bg-green-50 border border-green-200" data-testid="email-sent-notice">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <CheckCircle className="h-4 w-4" />
                    <span className="font-semibold text-sm">Reset Email Sent</span>
                  </div>
                  <p className="text-green-800/70 text-sm">
                    We've sent a password reset link to your email. Click the link in the email to reset your password.
                  </p>
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-5">
                {!tokenInput && (
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm tracking-wider uppercase">Reset Code</Label>
                    <Input
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      placeholder="Paste your reset code from email"
                      required
                      className="bg-white border-slate-200 text-[#0F172A] placeholder:text-slate-300 h-12 font-mono text-sm focus:border-[#E07A3A] focus:ring-[#E07A3A]"
                      data-testid="reset-token-input"
                    />
                  </div>
                )}
                {tokenInput && (
                  <input type="hidden" value={tokenInput} data-testid="reset-token-input" />
                )}
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm tracking-wider uppercase">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                    minLength={6}
                    className="bg-white border-slate-200 text-[#0F172A] placeholder:text-slate-300 h-12 focus:border-[#E07A3A] focus:ring-[#E07A3A]"
                    data-testid="reset-new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm tracking-wider uppercase">Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    minLength={6}
                    className="bg-white border-slate-200 text-[#0F172A] placeholder:text-slate-300 h-12 focus:border-[#E07A3A] focus:ring-[#E07A3A]"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 text-sm tracking-widest uppercase"
                  data-testid="reset-submit-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}

          {/* Step 3: Success */}
          {step === 'done' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-50 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <p className="text-slate-500 mb-8">
                You can now sign in with your new password.
              </p>
              <Link to="/login">
                <Button
                  className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 text-sm tracking-widest uppercase"
                  data-testid="go-to-login-btn"
                >
                  Go to Sign In
                </Button>
              </Link>
            </div>
          )}

          {step !== 'done' && (
            <div className="mt-8 text-center">
              <p className="text-slate-400">
                Remember your password?{' '}
                <Link
                  to="/login"
                  className="text-[#E07A3A] hover:text-[#C4652E] transition-colors font-medium"
                >
                  Sign In
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>

      <div
        className="hidden lg:block w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, #F8FAFC, transparent 50%), url('https://images.unsplash.com/photo-1757262798620-c2cc40cfb440?w=1200&auto=format&fit=crop')`
        }}
      />
    </div>
  );
};

export default ForgotPasswordPage;
